import React, { useContext, useMemo } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useScreenSize } from "shared/lib/useScreenSize"
import { IconSize } from "shared/types/Icon"
import { WithClassName } from "shared/types/WithClassName"
import { Button, ButtonProps, ButtonVariant } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"

interface OverlayHeaderProps extends WithClassName {
  prefix?: string
  responsive?: boolean
  children: React.ReactNode | React.ReactNode[]
}

interface OverlayHeaderContextType {
  prefix?: string
  responsive?: boolean
}

const OverlayHeaderContext = React.createContext<OverlayHeaderContextType>({})

const OverlayHeaderTitle = React.memo(
  ({ children }: { children: React.ReactNode }) => {
    return <h2 className="overlay-header__title h3">{children}</h2>
  }
)

const OverlayHeaderAction = React.memo((props: ButtonProps) => {
  return (
    <Button
      variant={ButtonVariant.SECONDARY}
      {...props}
      className={classNames(
        "btn--borderless",
        "overlay-header__controls",
        props.className
      )}
    />
  )
})

const OverlayHeaderClose = React.memo((props: ButtonProps) => {
  const { t } = useTranslation()
  const { prefix, responsive } = useContext(OverlayHeaderContext)
  const { isTablet } = useScreenSize()
  const { closeOverlay } = useCurrentOverlay()
  const { label, onClick } = props

  return (
    <Button
      variant={ButtonVariant.SECONDARY}
      id={prefix ? `${prefix}__close` : "overlay__close"}
      data-testid={prefix ? `${prefix}__close` : "overlay__close"}
      iconSize={IconSize.MEDIUM}
      icon={isTablet && responsive ? undefined : IconName.CLOSE}
      isIconOnly={!isTablet || !responsive}
      {...props}
      // An explicit handler wins: FormOverlay routes this through onCancel,
      // which also resets form state, and stories pass action() spies.
      onClick={onClick || closeOverlay}
      label={label || t("General.Close")}
      className={classNames(
        "btn--borderless",
        "overlay-header__controls",
        "close",
        props.className
      )}
    />
  )
})

const OverlayHeaderComponent = React.memo(
  ({ prefix, className, responsive = true, children }: OverlayHeaderProps) => {
    const context = useMemo<OverlayHeaderContextType>(
      () => ({
        prefix,
        responsive
      }),
      [prefix, responsive]
    )

    return (
      <OverlayHeaderContext.Provider value={context}>
        <div
          className={classNames("overlay-header", { responsive }, className)}
          data-testid="overlay-header"
        >
          {children}
        </div>
      </OverlayHeaderContext.Provider>
    )
  }
)

export const OverlayHeader = {
  Container: OverlayHeaderComponent,
  Title: OverlayHeaderTitle,
  Action: OverlayHeaderAction,
  CloseButton: OverlayHeaderClose
}
